import {useCallback, useState} from 'react';
import type {ColumnId} from '../components/Container/Progress/ProgressKanbanBoard';
import {COLUMN_CONFIG} from '../components/Container/Progress/ProgressKanbanBoard';

/** SP版のステータスフィルター用の型（'all'を含む） */
export type SpStatusFilter = ColumnId | 'all';

/** SP版フィルター用の設定（'all'を含む） */
export const SP_FILTER_CONFIG: Record<SpStatusFilter, {title: string}> = {
  all: {title: 'すべて'},
  activeNotCompleted: {title: COLUMN_CONFIG.activeNotCompleted.title},
  activeCompleted: {title: COLUMN_CONFIG.activeCompleted.title},
  completed: {title: COLUMN_CONFIG.completed.title},
  archived: {title: COLUMN_CONFIG.archived.title},
  rejected: {title: COLUMN_CONFIG.rejected.title},
};

/**
 * SP版のステータスフィルター値をAPI用のフィルター条件に変換する
 *
 * @param status - SP版のステータスフィルター値
 * @returns API呼び出し用のフィルター条件
 *
 * @example
 * convertSpStatusToFilterValues('activeNotCompleted')
 * // => { overallStatus: ['active'], hasCompletedEntry: false }
 */
export const convertSpStatusToFilterValues = (
  status: SpStatusFilter,
): {
  overallStatus?: ('active' | 'completed' | 'archived' | 'rejected')[];
  hasCompletedEntry?: boolean;
} => {
  switch (status) {
    case 'all':
      return {};
    case 'activeNotCompleted':
      return {overallStatus: ['active'], hasCompletedEntry: false};
    case 'activeCompleted':
      return {overallStatus: ['active'], hasCompletedEntry: true};
    case 'completed':
      return {overallStatus: ['completed']};
    case 'archived':
      return {overallStatus: ['archived']};
    case 'rejected':
      return {overallStatus: ['rejected']};
    default:
      return {};
  }
};

/**
 * SP版選考管理のステータスフィルター状態管理フック
 *
 * @description
 * - デフォルトは「すべて」
 * - フィルター変更時にAPI呼び出しをトリガーするため、親コンポーネントで使用
 *
 * @returns selectedStatus - 現在選択中のステータス
 * @returns handleStatusChange - ステータス変更ハンドラー
 */
export const useProgressStatusFilter = () => {
  const [selectedStatus, setSelectedStatus] = useState<SpStatusFilter>('all');

  const handleStatusChange = useCallback((status: SpStatusFilter) => {
    setSelectedStatus(status);
  }, []);

  return {selectedStatus, handleStatusChange};
};
